<template>
  <div class="settled-list-wrapper">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />

    <!-- Executive Toast Notification -->
    <v-snackbar v-model="showsnackbar" color="#1e293b" right :timeout="timeout">
      <div class="d-flex align-center justify-space-between text-white" style="font-size: 13px;">
        <span>{{ msg }}</span>
        <v-btn small icon text @click="showsnackbar = false" class="ml-2">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <!-- Top Executive Action Header Card -->
    <v-card flat class="header-card pa-4 mb-4 rounded-lg">
      <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 16px;">
        <!-- Left: Back Button & Title -->
        <div class="d-flex align-center">
          <v-btn
            icon
            small
            class="back-btn mr-3"
            @click="goBack"
            title="Go Back"
          >
            <v-icon color="#0f172a" size="20">mdi-arrow-left</v-icon>
          </v-btn>
          <div class="header-icon-box mr-3">
            <v-icon color="#0f172a" size="22">mdi-cash-check</v-icon>
          </div>
          <div>
            <div class="d-flex align-center flex-wrap" style="gap: 8px;">
              <span class="page-title">PAYMENT &amp; SETTLEMENT HISTORY</span>
              <span class="supplier-code-badge" v-if="supplierDetail && supplierDetail.supplierCode">
                {{ supplierDetail.supplierCode }}
              </span>
            </div>
            <div class="page-subtitle">
              {{ supplierDetail && supplierDetail.supplierName ? supplierDetail.supplierName : 'Supplier' }}
              <span v-if="supplierDetail && supplierDetail.contactNumber" class="ml-2">
                &bull; {{ supplierDetail.contactNumber }}
              </span>
              <span class="ml-2 text-muted">
                &bull; Record of thankam payouts, rate fixations &amp; cash settlements
              </span>
            </div>
          </div>
        </div>

        <!-- Right: Actions (Refresh + New Settlement) -->
        <div class="d-flex flex-wrap align-center" style="gap: 10px;">
          <v-btn
            icon
            small
            title="Refresh History"
            class="refresh-icon-btn"
            @click="getSettledList"
          >
            <v-icon small color="#64748b">mdi-refresh</v-icon>
          </v-btn>

          <v-btn
            dark
            color="#9a6b27"
            class="btn-primary text-capitalize elevation-1"
            @click="goToPurchaseSettlement"
          >
            <v-icon left small>mdi-scale-balance</v-icon>
            Purchase Settlement
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- 4 KPI Overview Metric Cards -->
    <div class="kpi-grid mb-4">
      <!-- 1. Total Settlements -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">TOTAL SETTLEMENTS</div>
          <div class="kpi-value">{{ settledList ? settledList.length : 0 }}</div>
          <div class="kpi-hint">Recorded payment transactions</div>
        </div>
        <div class="kpi-icon-pill" style="background: #eff6ff; color: #2563eb;">
          <v-icon size="20" color="#2563eb">mdi-receipt-text-check-outline</v-icon>
        </div>
      </div>

      <!-- 2. Total Paid Amount -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">TOTAL PAID AMOUNT</div>
          <div class="kpi-value">₹ {{ formatIndianNumber(totalPayAmountSum) }}</div>
          <div class="kpi-hint">Cumulative payouts settled</div>
        </div>
        <div class="kpi-icon-pill" style="background: #f0fdf4; color: #16a34a;">
          <v-icon size="20" color="#16a34a">mdi-cash-multiple</v-icon>
        </div>
      </div>

      <!-- 3. Purchased Thankam -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">PURCHASED THANKAM</div>
          <div class="kpi-value">{{ formatWeight(totalThankamSum) }} <span class="kpi-unit">gm</span></div>
          <div class="kpi-hint">Total thankam weight settled</div>
        </div>
        <div class="kpi-icon-pill" style="background: #fefce8; color: #ca8a04;">
          <v-icon size="20" color="#ca8a04">mdi-gold</v-icon>
        </div>
      </div>

      <!-- 4. Latest Thankam Rate -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">LATEST THANKAM RATE</div>
          <div class="kpi-value">
            ₹ {{ formatIndianNumber(latestThankamRate) }}
            <span class="kpi-unit">/ gm</span>
          </div>
          <div class="kpi-hint">Last settlement rate</div>
        </div>
        <div class="kpi-icon-pill" style="background: #f8fafc; color: #0f172a; border: 1px solid #e2e8f0;">
          <v-icon size="20" color="#0f172a">mdi-trending-up</v-icon>
        </div>
      </div>
    </div>

    <!-- Master Data Table Card -->
    <v-card flat class="content-table-card rounded-lg overflow-hidden">
      <!-- Section Bar -->
      <div class="section-table-header px-4 py-3 d-flex flex-wrap align-center justify-space-between border-b">
        <div class="d-flex align-center">
          <v-icon small color="#0f172a" class="mr-2">mdi-history</v-icon>
          <span class="section-title">SETTLED PAYMENT TRANSACTIONS</span>
          <span class="count-badge ml-3" v-if="settledList && settledList.length > 0">
            {{ settledList.length }} {{ settledList.length === 1 ? 'Record' : 'Records' }}
          </span>
        </div>
      </div>

      <!-- Table View -->
      <div v-if="settledList && settledList.length > 0">
        <v-simple-table class="pro-settled-table">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left" style="width: 55px;">#</th>
                <th class="text-left" style="width: 140px;">Settled Date</th>
                <th class="text-right" style="width: 150px;">Thankam Rate</th>
                <th class="text-center" style="width: 130px;">Payment Type</th>
                <th class="text-right" style="width: 170px;">Pay Amount (₹)</th>
                <th class="text-right" style="width: 170px;">Purchased Thankam (gm)</th>
                <th class="text-right" style="width: 170px;">Balance Thankam (gm)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(value, i) in paginatedList" :key="value._id || i" class="table-row">
                <td class="text-muted font-weight-medium">
                  {{ (page - 1) * itemsPerPage + i + 1 }}
                </td>
                <td>
                  <div class="d-flex align-center date-cell">
                    <v-icon size="14" color="#94a3b8" class="mr-1">mdi-calendar-blank-outline</v-icon>
                    <span>{{ formatDate(value.create_date) }}</span>
                  </div>
                </td>
                <td class="text-right font-weight-medium text-slate-800">
                  ₹ {{ formatIndianNumber(value.thankamRate) }}
                </td>
                <td class="text-center">
                  <span :class="getPaymentBadgeClass(value.paymentType)">
                    {{ getPaymentTypeText(value.paymentType) }}
                  </span>
                </td>
                <td class="text-right font-weight-bold" style="color: #0f172a; font-size: 13.5px;">
                  ₹ {{ formatIndianNumber(value.payAmount) }}
                </td>
                <td class="text-right font-weight-bold" style="color: #16a34a;">
                  {{ formatWeight(value.totalThankamWeight) }}
                </td>
                <td class="text-right font-weight-medium text-slate-700">
                  {{ formatWeight(value.balanceThankam) }}
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <!-- Custom Pagination Bar -->
        <div class="table-pagination-bar px-4 py-3 d-flex flex-wrap align-center justify-space-between">
          <div class="pagination-info">
            Showing
            <span class="font-weight-bold text-slate-800">{{ showingFrom }}</span>
            to
            <span class="font-weight-bold text-slate-800">{{ showingTo }}</span>
            of
            <span class="font-weight-bold text-slate-800">{{ settledList.length }}</span>
            settlements
          </div>

          <v-pagination
            v-if="totalPages > 1"
            v-model="page"
            :length="totalPages"
            :total-visible="7"
            circle
            color="#9a6b27"
            class="custom-pagination"
          ></v-pagination>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state-wrap text-center pa-8">
        <div class="empty-icon-circle mb-3">
          <v-icon size="32" color="#94a3b8">mdi-cash-remove</v-icon>
        </div>
        <div class="empty-title">No Settlement Payments Recorded</div>
        <div class="empty-subtitle">
          There are no thankam settlement transactions or payment disbursements recorded for this supplier yet.
        </div>
        <div class="mt-4">
          <v-btn
            small
            dark
            color="#9a6b27"
            class="btn-primary text-capitalize"
            @click="goToPurchaseSettlement"
          >
            <v-icon left small>mdi-scale-balance</v-icon>
            Initiate Settlement
          </v-btn>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "SettledList",
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2000,
      msg: null,
      page: 1,
      itemsPerPage: 20,
      settledList: [],
      supplierDetail: {},
    };
  },
  mounted() {
    this.getSupplierDetail();
    this.getSettledList();
  },
  computed: {
    appUser() {
      return this.$store.state.userData;
    },
    paginatedList() {
      if (!this.settledList || !this.settledList.length) return [];
      const start = (this.page - 1) * this.itemsPerPage;
      return this.settledList.slice(start, start + this.itemsPerPage);
    },
    totalPages() {
      if (!this.settledList || !this.settledList.length) return 1;
      return Math.max(1, Math.ceil(this.settledList.length / this.itemsPerPage));
    },
    totalPayAmountSum() {
      if (!this.settledList || !this.settledList.length) return 0;
      return this.settledList.reduce((acc, curr) => {
        const amt = curr && curr.payAmount ? Number(curr.payAmount) : 0;
        return acc + (isNaN(amt) ? 0 : amt);
      }, 0);
    },
    totalThankamSum() {
      if (!this.settledList || !this.settledList.length) return 0;
      return this.settledList.reduce((acc, curr) => {
        const wt = curr && curr.totalThankamWeight ? Number(curr.totalThankamWeight) : 0;
        return acc + (isNaN(wt) ? 0 : wt);
      }, 0);
    },
    latestThankamRate() {
      if (!this.settledList || !this.settledList.length) return 0;
      const first = this.settledList[0];
      return first && first.thankamRate ? Number(first.thankamRate) : 0;
    },
    showingFrom() {
      if (!this.settledList || this.settledList.length === 0) return 0;
      return (this.page - 1) * this.itemsPerPage + 1;
    },
    showingTo() {
      if (!this.settledList || this.settledList.length === 0) return 0;
      return Math.min(this.page * this.itemsPerPage, this.settledList.length);
    },
  },
  methods: {
    getSupplierDetail() {
      if (!this.$route.query.id) return;
      axios({
        method: "get",
        url: "/supplier/view",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          id: this.$route.query.id,
        },
      })
        .then((response) => {
          if (response.data && response.data.status === true) {
            this.supplierDetail = response.data.data || {};
          }
        })
        .catch((err) => {
          console.error("Error loading supplier profile:", err);
        });
    },
    getSettledList() {
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/supplier/based/paymenthistory",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          page: this.page,
          count: 50,
          supplierId: this.$route.query.id,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data && response.data.status === true) {
            this.settledList = response.data.ItemPaymentDetails || [];
          } else {
            this.showsnackbar = true;
            this.msg = response.data ? response.data.msg : "Failed to load settlement history";
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error("Error loading settled list:", err);
        });
    },
    goBack() {
      if (this.$route.query.id) {
        this.$router.push("/SupplierDetail?id=" + this.$route.query.id);
      } else {
        this.$router.go(-1);
      }
    },
    goToPurchaseSettlement() {
      const name = this.supplierDetail && this.supplierDetail.supplierName ? this.supplierDetail.supplierName : "";
      this.$router.push("/supplierSettlement?id=" + this.$route.query.id + "&&name=" + encodeURIComponent(name));
    },
    formatDate(item) {
      if (!item) return "-";
      try {
        const dt = new Date(item);
        if (isNaN(dt.getTime())) return "-";
        const day = dt.getDate();
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const month = months[dt.getMonth()];
        const year = dt.getFullYear();
        return `${day} ${month} ${year}`;
      } catch (e) {
        return "-";
      }
    },
    formatIndianNumber(amount) {
      if (amount === undefined || amount === null || isNaN(amount)) return "0.00";
      const val = Number(amount);
      return val.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatWeight(wt) {
      if (wt === undefined || wt === null || isNaN(wt)) return "0.00";
      const val = Number(wt);
      return val.toFixed(2);
    },
    getPaymentTypeText(val) {
      if (Array.isArray(val) && val.length > 0) {
        return val[0] || "Cash";
      }
      if (typeof val === "string" && val.trim()) {
        return val;
      }
      return "Cash";
    },
    getPaymentBadgeClass(val) {
      const type = this.getPaymentTypeText(val).toLowerCase();
      if (type.includes("cash")) return "pay-badge-cash";
      if (type.includes("thankam") || type.includes("gold")) return "pay-badge-thankam";
      if (type.includes("bank") || type.includes("neft") || type.includes("online")) return "pay-badge-bank";
      return "pay-badge-default";
    },
  },
};
</script>

<style scoped>
.settled-list-wrapper {
  padding: 4px 0 40px 0;
  width: 100%;
  margin: 0 auto;
  font-family: "Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  color: #0f172a;
}

/* Header Card */
.header-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
}

.back-btn {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
}

.back-btn:hover {
  background: #f8fafc;
}

.header-icon-box {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.5px;
}

.supplier-code-badge {
  font-family: monospace;
  font-weight: 700;
  font-size: 11px;
  background: #f1f5f9;
  color: #0f172a;
  padding: 2px 7px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.page-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 1px;
}

.btn-primary {
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  background-color: #9a6b27 !important;
  color: #ffffff !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  height: 38px !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 14px rgba(154, 106, 44, 0.28) !important;
  transition: all 0.2s ease !important;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #c59449 0%, #956627 100%) !important;
  box-shadow: 0 6px 18px rgba(154, 106, 44, 0.38) !important;
  transform: translateY(-1.5px);
}

.refresh-icon-btn {
  border: 1px solid #cbd5e1 !important;
  border-radius: 8px !important;
  background: #ffffff !important;
  height: 38px !important;
  width: 38px !important;
}

.refresh-icon-btn:hover {
  background: #f8fafc !important;
}

/* 4 KPI Metrics Grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

@media (max-width: 1280px) {
  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }
}

.kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  transition: all 0.2s ease;
}

.kpi-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.kpi-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.kpi-value {
  font-size: 19px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.3px;
  margin: 3px 0;
  line-height: 1.2;
}

.kpi-unit {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.kpi-hint {
  font-size: 11px;
  color: #94a3b8;
}

.kpi-icon-pill {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Master Settled Table Card */
.content-table-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
}

.section-table-header {
  background: #f8fafc;
}

.section-title {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.5px;
}

.count-badge {
  font-size: 11px;
  background: #e2e8f0;
  color: #475569;
  padding: 2px 7px;
  border-radius: 12px;
  font-weight: 600;
}

.pro-settled-table :deep(table) {
  width: 100%;
  border-collapse: collapse;
}

.pro-settled-table :deep(th) {
  background-color: #ffffff !important;
  color: #475569 !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  padding: 12px 16px !important;
  border-bottom: 1px solid #e2e8f0 !important;
  white-space: nowrap;
}

.pro-settled-table :deep(td) {
  font-size: 13px !important;
  color: #334155 !important;
  padding: 12px 16px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.pro-settled-table :deep(tr:hover) {
  background-color: #f8fafc !important;
}

.date-cell {
  font-size: 12.5px;
  font-weight: 500;
  color: #475569;
}

/* Payment Badges */
.pay-badge-cash {
  font-size: 10.5px;
  font-weight: 700;
  background: #f0fdf4;
  color: #15803d;
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid #bbf7d0;
}

.pay-badge-thankam {
  font-size: 10.5px;
  font-weight: 700;
  background: #fefce8;
  color: #b45309;
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid #fef08a;
}

.pay-badge-bank {
  font-size: 10.5px;
  font-weight: 700;
  background: #eff6ff;
  color: #1d4ed8;
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid #dbeafe;
}

.pay-badge-default {
  font-size: 10.5px;
  font-weight: 700;
  background: #f1f5f9;
  color: #475569;
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
}

/* Pagination Bar */
.table-pagination-bar {
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
}

.pagination-info {
  font-size: 12.5px;
  color: #64748b;
}

.custom-pagination :deep(.v-pagination__item) {
  font-size: 12px !important;
  min-width: 28px !important;
  height: 28px !important;
  border-radius: 6px !important;
  box-shadow: none !important;
  border: 1px solid #e2e8f0 !important;
}

.custom-pagination :deep(.v-pagination__navigation) {
  box-shadow: none !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 6px !important;
  width: 28px !important;
  height: 28px !important;
}

/* Empty State */
.empty-state-wrap {
  padding: 50px 20px;
}

.empty-icon-circle {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.empty-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.empty-subtitle {
  font-size: 12.5px;
  color: #64748b;
  margin-top: 4px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.border-b {
  border-bottom: 1px solid #e2e8f0;
}

.text-muted {
  color: #94a3b8;
}

.text-slate-700 {
  color: #334155;
}

.text-slate-800 {
  color: #1e293b;
}
</style>